import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllTables, restaurantTables } from '../data/restaurantTables';
import { Users, Check, ArrowRight, ArrowLeft, Plus, Minus, X, Lightbulb, AlertTriangle, Sparkles } from 'lucide-react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const CombineTableBooking = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [selectedTables, setSelectedTables] = useState([]);
  const [bookingData, setBookingData] = useState({
    name: '',
    phone: '',
    email: '',
    date: '',
    time: '',
    guests: 2,
    specialRequests: ''
  });
  const [bookedTables, setBookedTables] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);

  const allTables = getAllTables();

  // Calculate total selected capacity
  const totalSelectedCapacity = selectedTables.reduce((sum, table) => sum + table.capacity, 0);
  const guestsNeeded = bookingData.guests;
  
  // Buffer: Allow 1-2 extra seats (to accommodate exact matches)
  const maxAllowedCapacity = guestsNeeded + 2;
  const hasEnoughSeats = totalSelectedCapacity >= guestsNeeded;
  const isOverCapacity = totalSelectedCapacity > maxAllowedCapacity;
  const isExactOrSlightlyOver = hasEnoughSeats && !isOverCapacity;
  const remainingNeeded = Math.max(0, guestsNeeded - totalSelectedCapacity);

  // Group tables by area for display
  const tableAreas = [
    {
      id: 'window',
      name: 'Window Tables',
      icon: '🪟',
      description: 'Street view with natural light',
      color: 'blue',
      photo: 'https://customer-assets.emergentagent.com/job_8f790146-acd4-4a9a-93d7-80916b412d08/artifacts/obvc9vi5_Windows%20table%20next%20to%20door%204%2B4.jpg',
      tables: restaurantTables.windowTables
    },
    {
      id: 'cabinet',
      name: 'Cabinet Area',
      icon: '🏔️',
      description: 'Mountain art wall, quiet seating',
      color: 'cyan',
      photo: 'https://customer-assets.emergentagent.com/job_8f790146-acd4-4a9a-93d7-80916b412d08/artifacts/l4ctlh45_cabinet%201%20full%20photo.jpg',
      tables: [...restaurantTables.cabinet1Area, ...restaurantTables.cabinet1WindowTable]
    },
    {
      id: 'booth',
      name: 'Corner Sofa Booth',
      icon: '🛋️',
      description: 'Comfortable leather booth',
      color: 'purple',
      photo: 'https://customer-assets.emergentagent.com/job_8f790146-acd4-4a9a-93d7-80916b412d08/artifacts/fp25o0oh_corner%20sofa%20table%206%2B4.jpg',
      tables: restaurantTables.cornerSofaBooth
    },
    {
      id: 'group',
      name: 'Center Group Table',
      icon: '👥',
      description: 'Large 12-seat table for celebrations',
      color: 'green',
      photo: 'https://customer-assets.emergentagent.com/job_8f790146-acd4-4a9a-93d7-80916b412d08/artifacts/lkwpq49f_group%20table%20of%2012.jpg',
      tables: restaurantTables.centerGroupTable
    },
    {
      id: 'hut',
      name: 'Decorative Hut (VIP)',
      icon: '✨',
      description: 'Traditional carved wood, private setting',
      color: 'amber',
      photo: 'https://customer-assets.emergentagent.com/job_8f790146-acd4-4a9a-93d7-80916b412d08/artifacts/l3hnz07u_decorative%20hut%20tables%202%2B2.jpg',
      tables: restaurantTables.decorativeHut
    }
  ];

  // Generate smart suggestions - find best combinations from same area
  const suggestions = useMemo(() => {
    if (guestsNeeded <= 0) return [];
    
    const results = [];
    
    tableAreas.forEach(area => {
      const availableTables = area.tables.filter(t => !bookedTables.includes(t.id));
      if (availableTables.length === 0) return;
      
      // Try to find combinations that match or slightly exceed guest count
      const findCombinations = (tables, target, current = [], currentSum = 0) => {
        // If we've reached or slightly exceeded target, save this combination
        if (currentSum >= target && currentSum <= target + 2) {
          return [current];
        }
        if (currentSum > target + 2) return [];
        
        let combos = [];
        for (let i = 0; i < tables.length; i++) {
          const newCombos = findCombinations(
            tables.slice(i + 1),
            target,
            [...current, tables[i]],
            currentSum + tables[i].capacity
          );
          combos = [...combos, ...newCombos];
        }
        return combos;
      };
      
      const areaCombinations = findCombinations(availableTables, guestsNeeded);
      
      // Take top 2 combinations per area (smallest that meets requirement)
      const sortedCombos = areaCombinations
        .sort((a, b) => {
          const sumA = a.reduce((s, t) => s + t.capacity, 0);
          const sumB = b.reduce((s, t) => s + t.capacity, 0);
          // Prefer exact match, then smallest over
          const diffA = Math.abs(sumA - guestsNeeded);
          const diffB = Math.abs(sumB - guestsNeeded);
          return diffA - diffB;
        })
        .slice(0, 2);
      
      sortedCombos.forEach(combo => {
        const totalCap = combo.reduce((s, t) => s + t.capacity, 0);
        results.push({
          area: area.name,
          areaIcon: area.icon,
          areaId: area.id,
          tables: combo,
          totalCapacity: totalCap,
          tableCount: combo.length,
          description: combo.map(t => `${t.name} (${t.capacity})`).join(' + ')
        });
      });
    });
    
    // Sort by how close to exact match
    return results
      .sort((a, b) => Math.abs(a.totalCapacity - guestsNeeded) - Math.abs(b.totalCapacity - guestsNeeded))
      .slice(0, 4);
  }, [guestsNeeded, bookedTables, tableAreas]);

  useEffect(() => {
    if (step === 2 && bookingData.date && bookingData.time) {
      fetchBookedTables();
    }
  }, [step, bookingData.date, bookingData.time]);

  const fetchBookedTables = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/table-reservations`, {
        params: { date: bookingData.date, time: bookingData.time }
      });
      setBookedTables(response.data.bookedTableIds || []);
    } catch (error) {
      console.error('Error fetching booked tables:', error);
    }
  };

  const isTableAvailable = (tableId) => !bookedTables.includes(tableId);
  const isTableSelected = (tableId) => selectedTables.some(t => t.id === tableId);

  // Check if adding this table would exceed max capacity
  const wouldExceedCapacity = (table) => {
    if (isTableSelected(table.id)) return false; // Can always deselect
    return (totalSelectedCapacity + table.capacity) > maxAllowedCapacity;
  };

  const handleStep1Submit = (e) => {
    e.preventDefault();
    if (!bookingData.name || !bookingData.phone || !bookingData.email || !bookingData.date || !bookingData.time) {
      alert('Please fill all required fields');
      return;
    }
    setStep(2);
  };

  const toggleTableSelection = (table) => {
    if (!isTableAvailable(table.id)) {
      return; // Can't select booked tables
    }

    if (isTableSelected(table.id)) {
      // Remove from selection
      setSelectedTables(prev => prev.filter(t => t.id !== table.id));
    } else {
      // Check if adding would exceed capacity
      if (wouldExceedCapacity(table)) {
        return; // Don't allow over-selection
      }
      // Add to selection
      setSelectedTables(prev => [...prev, table]);
    }
  };

  const applySuggestion = (suggestion) => {
    setSelectedTables(suggestion.tables);
    setShowSuggestions(false);
  };

  const clearSelection = () => {
    setSelectedTables([]);
    setShowSuggestions(true);
  };

  const handleFinalSubmit = async () => {
    if (selectedTables.length === 0) {
      alert('Please select at least one table');
      return;
    }
    if (!hasEnoughSeats) {
      alert(`You need ${guestsNeeded} seats but only selected ${totalSelectedCapacity}. Please select more tables.`);
      return;
    }

    setIsSubmitting(true);
    try {
      const reservationData = {
        ...bookingData,
        tableIds: selectedTables.map(t => t.id),
        tableNames: selectedTables.map(t => t.name).join(' + '),
        tableCapacity: totalSelectedCapacity,
        combinedTables: selectedTables.map(t => ({
          id: t.id,
          name: t.name,
          capacity: t.capacity,
          type: t.type
        })),
        status: 'confirmed',
        createdAt: new Date().toISOString()
      };

      const response = await axios.post(`${BACKEND_URL}/api/combined-table-reservations`, reservationData);
      if (response.data.success) {
        setBookingSuccess(true);
        setTimeout(() => navigate('/'), 4000);
      }
    } catch (error) {
      console.error('Error creating reservation:', error);
      if (error.response && error.response.status === 400) {
        alert(error.response.data.detail || 'One or more tables are already booked. Please refresh and try again.');
        fetchBookedTables();
        setSelectedTables([]);
      } else {
        alert('Error creating reservation. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Color mappings
  const colorClasses = {
    blue: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-600', light: 'bg-blue-100' },
    cyan: { bg: 'bg-cyan-50', border: 'border-cyan-200', text: 'text-cyan-600', light: 'bg-cyan-100' },
    purple: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-600', light: 'bg-purple-100' },
    green: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-600', light: 'bg-green-100' },
    amber: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-600', light: 'bg-amber-100' }
  };

  if (bookingSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-[#F5F3EF]">
        <motion.div 
          className="bg-white rounded-2xl p-12 text-center max-w-2xl shadow-xl border border-[#2F2F2F]/10"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
        >
          <motion.div 
            className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 bg-green-500"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring' }}
          >
            <Check className="w-12 h-12 text-white" />
          </motion.div>
          <h2 className="text-3xl font-bold mb-4 text-[#2F2F2F]" style={{ fontFamily: 'DM Serif Display, serif' }}>
            Reservation Confirmed!
          </h2>
          
          <div className="mb-6 p-4 rounded-xl bg-[#C97A2B]/10 border border-[#C97A2B]/30">
            <p className="font-bold mb-2 text-[#C97A2B]">
              Tables Reserved
            </p>
            <p className="text-[#2F2F2F]">
              {selectedTables.map(t => t.name).join(' + ')}
            </p>
            <p className="text-[#6B705C] text-sm mt-1">
              Total: {totalSelectedCapacity} seats for {guestsNeeded} guests
            </p>
          </div>
          
          <p className="text-[#2F2F2F] text-lg mb-2">Thank you, {bookingData.name}!</p>
          <p className="text-[#6B705C]">{bookingData.date} at {bookingData.time}</p>
          <p className="text-[#6B705C]/60 mt-4 text-sm">Redirecting to home...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F3EF]">
      <div className="max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-40">
        {/* Header */}
        <div className="text-center mb-8 md:mb-12">
          <motion.p 
            className="text-[#C97A2B] text-xs tracking-[0.3em] uppercase mb-2"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            Table Reservation
          </motion.p>
          <motion.h1 
            className="text-3xl md:text-5xl text-[#2F2F2F] mb-4"
            style={{ fontFamily: 'DM Serif Display, serif' }}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            Combine Tables for Your <span className="italic text-[#C97A2B]">Group</span>
          </motion.h1>
          <p className="text-[#6B705C] max-w-xl mx-auto text-sm md:text-base">
            Select tables to accommodate your party - we'll help you find the best combination
          </p>
          
          {/* Step Indicator */}
          <div className="flex justify-center gap-4 mt-6 md:mt-8">
            <div className={`flex items-center gap-2 ${step >= 1 ? 'text-[#C97A2B]' : 'text-[#6B705C]'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= 1 ? 'bg-[#C97A2B] text-white' : 'bg-[#D9D9D6] text-[#6B705C]'}`}>
                {step > 1 ? <Check className="w-4 h-4" /> : '1'}
              </div>
              <span className="font-medium text-sm">Details</span>
            </div>
            <div className="w-8 md:w-12 border-t-2 border-[#D9D9D6] self-center"></div>
            <div className={`flex items-center gap-2 ${step >= 2 ? 'text-[#C97A2B]' : 'text-[#6B705C]'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= 2 ? 'bg-[#C97A2B] text-white' : 'bg-[#D9D9D6] text-[#6B705C]'}`}>
                2
              </div>
              <span className="font-medium text-sm">Select Tables</span>
            </div>
          </div>
        </div>

        {/* Step 1: Booking Details */}
        {step === 1 && (
          <motion.div 
            className="max-w-2xl mx-auto"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-[#2F2F2F]/10">
              <h2 className="text-xl md:text-2xl font-bold text-[#2F2F2F] mb-6" style={{ fontFamily: 'DM Serif Display, serif' }}>
                Reservation Details
              </h2>
              <form onSubmit={handleStep1Submit} className="space-y-5">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[#2F2F2F] font-medium mb-2 text-sm">Date *</label>
                    <input 
                      type="date" 
                      value={bookingData.date} 
                      min={new Date().toISOString().split('T')[0]} 
                      onChange={(e) => setBookingData({...bookingData, date: e.target.value})} 
                      className="w-full px-4 py-3 bg-[#F5F3EF] border border-[#D9D9D6] rounded-lg text-[#2F2F2F] focus:outline-none focus:border-[#C97A2B]" 
                      required 
                      data-testid="date-input"
                    />
                  </div>
                  <div>
                    <label className="block text-[#2F2F2F] font-medium mb-2 text-sm">Time *</label>
                    <select 
                      value={bookingData.time} 
                      onChange={(e) => setBookingData({...bookingData, time: e.target.value})} 
                      className="w-full px-4 py-3 bg-[#F5F3EF] border border-[#D9D9D6] rounded-lg text-[#2F2F2F] focus:outline-none focus:border-[#C97A2B]" 
                      required
                      data-testid="time-input"
                    >
                      <option value="">Select time</option>
                      {['11:00', '11:30', '12:00', '12:30', '13:00', '13:30', '14:00', '14:30', '17:00', '17:30', '18:00', '18:30', '19:00', '19:30', '20:00', '20:30', '21:00'].map(time => (
                        <option key={time} value={time}>{time}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-[#2F2F2F] font-medium mb-2 text-sm">Number of Guests *</label>
                  <div className="flex items-center gap-4">
                    <button 
                      type="button"
                      onClick={() => setBookingData({...bookingData, guests: Math.max(1, bookingData.guests - 1)})}
                      className="w-12 h-12 rounded-full bg-[#F5F3EF] border border-[#D9D9D6] flex items-center justify-center hover:border-[#C97A2B] transition-colors"
                      data-testid="guests-minus"
                    >
                      <Minus className="w-5 h-5 text-[#2F2F2F]" />
                    </button>
                    <span className="text-3xl font-bold text-[#2F2F2F] min-w-[60px] text-center" data-testid="guests-count">{bookingData.guests}</span>
                    <button 
                      type="button"
                      onClick={() => setBookingData({...bookingData, guests: bookingData.guests + 1})}
                      className="w-12 h-12 rounded-full bg-[#F5F3EF] border border-[#D9D9D6] flex items-center justify-center hover:border-[#C97A2B] transition-colors"
                      data-testid="guests-plus"
                    >
                      <Plus className="w-5 h-5 text-[#2F2F2F]" />
                    </button>
                  </div>
                  <p className="text-[#6B705C] text-xs mt-2">We'll suggest the best table combinations for your group</p>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[#2F2F2F] font-medium mb-2 text-sm">Name *</label>
                    <input 
                      type="text" 
                      value={bookingData.name} 
                      onChange={(e) => setBookingData({...bookingData, name: e.target.value})} 
                      className="w-full px-4 py-3 bg-[#F5F3EF] border border-[#D9D9D6] rounded-lg text-[#2F2F2F] focus:outline-none focus:border-[#C97A2B]" 
                      placeholder="Your full name"
                      required 
                      data-testid="name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-[#2F2F2F] font-medium mb-2 text-sm">Phone *</label>
                    <input 
                      type="tel" 
                      value={bookingData.phone} 
                      onChange={(e) => setBookingData({...bookingData, phone: e.target.value})} 
                      className="w-full px-4 py-3 bg-[#F5F3EF] border border-[#D9D9D6] rounded-lg text-[#2F2F2F] focus:outline-none focus:border-[#C97A2B]" 
                      placeholder="+358 40 123 4567"
                      required 
                      data-testid="phone-input"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-[#2F2F2F] font-medium mb-2 text-sm">Email *</label>
                  <input 
                    type="email" 
                    value={bookingData.email} 
                    onChange={(e) => setBookingData({...bookingData, email: e.target.value})} 
                    className="w-full px-4 py-3 bg-[#F5F3EF] border border-[#D9D9D6] rounded-lg text-[#2F2F2F] focus:outline-none focus:border-[#C97A2B]" 
                    placeholder="your@email.com"
                    required 
                    data-testid="email-input"
                  />
                </div>
                
                <div>
                  <label className="block text-[#2F2F2F] font-medium mb-2 text-sm">Special Requests (Optional)</label>
                  <textarea 
                    value={bookingData.specialRequests} 
                    onChange={(e) => setBookingData({...bookingData, specialRequests: e.target.value})} 
                    className="w-full px-4 py-3 bg-[#F5F3EF] border border-[#D9D9D6] rounded-lg text-[#2F2F2F] focus:outline-none focus:border-[#C97A2B]" 
                    rows="3" 
                    placeholder="Birthday celebration, dietary requirements, etc." 
                    data-testid="special-requests"
                  />
                </div>
                
                <button 
                  type="submit" 
                  className="w-full py-4 bg-[#2F2F2F] hover:bg-[#C97A2B] text-white font-medium text-sm tracking-wider uppercase rounded-lg transition-colors flex items-center justify-center gap-2"
                  data-testid="continue-btn"
                >
                  Continue to Table Selection <ArrowRight className="w-4 h-4" />
                </button>
              </form>
            </div>
          </motion.div>
        )}

        {/* Step 2: Table Selection */}
        {step === 2 && (
          <motion.div 
            className="space-y-6"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <button 
              onClick={() => setStep(1)} 
              className="flex items-center gap-2 text-[#6B705C] hover:text-[#2F2F2F] transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4" /> Back to Edit Details
            </button>
            
            {/* Reservation Summary */}
            <div className="bg-white rounded-xl p-4 md:p-6 border border-[#2F2F2F]/10 shadow-sm">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-[#2F2F2F]">
                <div>
                  <p className="text-[#6B705C] text-xs uppercase tracking-wider">Date</p>
                  <p className="font-semibold">{bookingData.date}</p>
                </div>
                <div>
                  <p className="text-[#6B705C] text-xs uppercase tracking-wider">Time</p>
                  <p className="font-semibold">{bookingData.time}</p>
                </div>
                <div>
                  <p className="text-[#6B705C] text-xs uppercase tracking-wider">Guests</p>
                  <p className="font-semibold text-[#C97A2B]">{guestsNeeded} people</p>
                </div>
                <div>
                  <p className="text-[#6B705C] text-xs uppercase tracking-wider">Name</p>
                  <p className="font-semibold">{bookingData.name}</p>
                </div>
              </div>
            </div>

            {/* Smart Suggestions */}
            {showSuggestions && suggestions.length > 0 && selectedTables.length === 0 && (
              <motion.div 
                className="bg-gradient-to-r from-[#C97A2B]/10 to-amber-50 rounded-xl p-4 md:p-6 border border-[#C97A2B]/30"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="w-5 h-5 text-[#C97A2B]" />
                  <h3 className="font-bold text-[#2F2F2F]">Recommended for {guestsNeeded} guests</h3>
                  <button 
                    onClick={() => setShowSuggestions(false)}
                    className="ml-auto text-[#6B705C] hover:text-[#2F2F2F]"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  {suggestions.map((suggestion, idx) => (
                    <motion.button
                      key={idx}
                      onClick={() => applySuggestion(suggestion)}
                      className="bg-white p-4 rounded-lg border-2 border-[#D9D9D6] hover:border-[#C97A2B] transition-all text-left group"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      data-testid={`suggestion-${idx}`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xl">{suggestion.areaIcon}</span>
                        <span className="font-medium text-[#2F2F2F] text-sm">{suggestion.area}</span>
                        <span className="ml-auto text-xs font-bold text-green-600 bg-green-100 px-2 py-1 rounded">
                          {suggestion.totalCapacity} seats
                        </span>
                      </div>
                      <p className="text-xs text-[#6B705C]">{suggestion.description}</p>
                      <p className="text-xs text-[#C97A2B] mt-2 group-hover:underline">
                        Click to select {suggestion.tableCount} table{suggestion.tableCount > 1 ? 's' : ''}
                      </p>
                    </motion.button>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Capacity Status Banner */}
            <div className={`rounded-xl p-4 border ${
              isExactOrSlightlyOver 
                ? 'bg-green-50 border-green-300' 
                : remainingNeeded > 0 
                ? 'bg-amber-50 border-amber-300' 
                : 'bg-red-50 border-red-300'
            }`}>
              <div className="flex items-center gap-3">
                {isExactOrSlightlyOver ? (
                  <Check className="w-6 h-6 text-green-600" />
                ) : remainingNeeded > 0 ? (
                  <Lightbulb className="w-6 h-6 text-amber-600" />
                ) : (
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                )}
                <div className="flex-1">
                  {isExactOrSlightlyOver ? (
                    <p className="font-medium text-green-800">
                      Perfect! You have {totalSelectedCapacity} seats for {guestsNeeded} guests
                    </p>
                  ) : remainingNeeded > 0 ? (
                    <p className="font-medium text-amber-800">
                      Select {remainingNeeded} more seat{remainingNeeded > 1 ? 's' : ''} to accommodate your group
                      <span className="block text-sm font-normal">Selected: {totalSelectedCapacity} / {guestsNeeded} needed</span>
                    </p>
                  ) : (
                    <p className="font-medium text-red-800">
                      Maximum reached - you can't select more tables
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Table Areas */}
            <div className="space-y-6">
              {tableAreas.map((area) => {
                const colors = colorClasses[area.color];
                const areaSelectedCount = selectedTables.filter(st => 
                  area.tables.some(at => at.id === st.id)
                ).length;
                
                return (
                  <div 
                    key={area.id} 
                    className="bg-white rounded-2xl overflow-hidden border border-[#2F2F2F]/10 shadow-sm"
                  >
                    {/* Area Header with Photo */}
                    <div className="relative h-32 md:h-40 overflow-hidden">
                      <img src={area.photo} alt={area.name} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent" />
                      <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-2xl">{area.icon}</span>
                            <span className="text-white font-bold text-lg md:text-xl">{area.name}</span>
                          </div>
                          <p className="text-white/70 text-xs md:text-sm">{area.description}</p>
                        </div>
                        {areaSelectedCount > 0 && (
                          <span className="bg-[#C97A2B] text-white text-xs font-bold px-3 py-1 rounded-full">
                            {areaSelectedCount} selected
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Tables in this area */}
                    <div className="p-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {area.tables.map(table => {
                          const isBooked = !isTableAvailable(table.id);
                          const isSelected = isTableSelected(table.id);
                          const isDisabled = isBooked || (wouldExceedCapacity(table) && !isSelected);
                          
                          return (
                            <button
                              key={table.id}
                              onClick={() => toggleTableSelection(table)}
                              disabled={isDisabled}
                              className={`p-3 rounded-lg border-2 transition-all flex items-center justify-between ${
                                isSelected
                                  ? 'bg-[#C97A2B]/20 border-[#C97A2B] ring-2 ring-[#C97A2B]/30'
                                  : isBooked
                                  ? 'bg-red-50 border-red-200 opacity-60 cursor-not-allowed'
                                  : isDisabled
                                  ? 'bg-gray-100 border-gray-200 opacity-50 cursor-not-allowed'
                                  : `${colors.bg} border-[#D9D9D6] hover:border-[#C97A2B] active:scale-98`
                              }`}
                              data-testid={`table-${table.id}`}
                            >
                              <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold text-lg ${
                                  isSelected 
                                    ? 'bg-[#C97A2B] text-white' 
                                    : isBooked 
                                    ? 'bg-red-200 text-red-600'
                                    : isDisabled
                                    ? 'bg-gray-200 text-gray-500'
                                    : `${colors.light} ${colors.text}`
                                }`}>
                                  {table.capacity}
                                </div>
                                <div className="text-left">
                                  <p className={`font-medium text-sm ${isSelected ? 'text-[#C97A2B]' : isDisabled ? 'text-gray-500' : 'text-[#2F2F2F]'}`}>
                                    {table.name}
                                  </p>
                                  <p className="text-xs text-[#6B705C]">{table.capacity} seats</p>
                                </div>
                              </div>
                              
                              <div className="flex items-center gap-2">
                                {isBooked && (
                                  <span className="text-xs font-bold text-red-500 bg-red-100 px-2 py-1 rounded">BOOKED</span>
                                )}
                                {isDisabled && !isBooked && (
                                  <span className="text-xs text-gray-500">Max reached</span>
                                )}
                                {isSelected && (
                                  <motion.div
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    className="w-6 h-6 rounded-full bg-[#C97A2B] flex items-center justify-center"
                                  >
                                    <Check className="w-4 h-4 text-white" />
                                  </motion.div>
                                )}
                                {!isBooked && !isSelected && !isDisabled && (
                                  <Plus className="w-5 h-5 text-[#6B705C]" />
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </div>

      {/* Fixed Bottom Bar - Selected Tables Summary */}
      {step === 2 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#2F2F2F]/10 shadow-lg z-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
            {/* Selected Tables Preview */}
            {selectedTables.length > 0 && (
              <div className="mb-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-[#2F2F2F]">Selected Tables:</p>
                  <button 
                    onClick={clearSelection}
                    className="text-xs text-red-500 hover:text-red-600 flex items-center gap-1"
                    data-testid="clear-all-btn"
                  >
                    <X className="w-3 h-3" /> Clear All
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  <AnimatePresence>
                    {selectedTables.map(table => (
                      <motion.div
                        key={table.id}
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0, opacity: 0 }}
                        className="flex items-center gap-2 bg-[#C97A2B]/10 border border-[#C97A2B]/30 rounded-full px-3 py-1"
                      >
                        <span className="text-sm text-[#2F2F2F]">{table.name}</span>
                        <span className="text-xs font-bold text-[#C97A2B]">({table.capacity})</span>
                        <button 
                          onClick={() => toggleTableSelection(table)}
                          className="w-4 h-4 rounded-full bg-[#C97A2B]/20 flex items-center justify-center hover:bg-[#C97A2B] hover:text-white transition-colors"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              </div>
            )}
            
            {/* Capacity Bar & Submit Button */}
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-[#6B705C]">Total Capacity</span>
                  <span className={`text-lg font-bold ${
                    isExactOrSlightlyOver ? 'text-green-600' : 'text-[#C97A2B]'
                  }`}>
                    {totalSelectedCapacity} / {guestsNeeded} needed
                  </span>
                </div>
                <div className="h-2 bg-[#D9D9D6] rounded-full overflow-hidden">
                  <motion.div 
                    className={`h-full rounded-full ${
                      isExactOrSlightlyOver ? 'bg-green-500' : 'bg-[#C97A2B]'
                    }`}
                    initial={{ width: 0 }}
                    animate={{ width: `${Math.min(100, (totalSelectedCapacity / guestsNeeded) * 100)}%` }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
              </div>
              
              <button 
                onClick={handleFinalSubmit}
                disabled={isSubmitting || selectedTables.length === 0 || !hasEnoughSeats}
                className={`px-6 md:px-8 py-3 rounded-lg font-medium text-sm tracking-wider uppercase flex items-center gap-2 transition-all ${
                  selectedTables.length > 0 && hasEnoughSeats
                    ? 'bg-[#2F2F2F] hover:bg-[#C97A2B] text-white'
                    : 'bg-[#D9D9D6] text-[#6B705C] cursor-not-allowed'
                }`}
                data-testid="confirm-booking-btn"
              >
                {isSubmitting ? (
                  'Confirming...'
                ) : (
                  <>
                    Confirm <Check className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CombineTableBooking;
